/**
 * Format hints for rendering a numeric column.
 *
 * Returned by {@link inferNumberFormat} so the column renderer (e.g. NumberCell)
 * can pick a decimal scale AND decide whether a thousand separator is appropriate.
 *
 * The two flags are independent: a year column wants `scale: 0` *and*
 * `useThousandSeparator: false` (so 2025 doesn't render as "2,025"), while a
 * count column also wants `scale: 0` but with the separator (so 12000 reads as
 * "12,000").
 */
export interface NumberFormatHints {
  /** Fixed number of decimal places to display. Omit to render the value's natural precision. */
  scale?: number;
  /** Whether to render a thousand separator (e.g. comma). */
  useThousandSeparator: boolean;
}
/**
 * Infers display formatting for a column of numeric values.
 *
 * Decision tree:
 * 1. All zeros / empty → no decimals, no separator.
 * 2. **Year-like** — all integers in 1000–9999 (and accessor name matches `/year/i`
 *    when provided) — 0 decimals, no separator. Avoids "2,025" for the year 2025.
 * 3. **Integer-only** — 0 decimals; separator only when magnitude ≥ 1000 (so
 *    counts like 12000 render as "12,000" but months 1–12 render as plain "8").
 * 4. **Mixed/decimal** — magnitude bands map to a fixed scale:
 *    - ≥1000 → 0 decimals (4248 → "4,248")
 *    - ≥100  → 1 decimal  (486.9)
 *    - ≥1    → 2 decimals (56.54)
 *    - ≥0.01 → 3 decimals (0.428)
 *    - <0.01 → 4 decimals (0.0042)
 *
 * The accessor parameter is optional. When provided it tightens the year-like
 * heuristic — purely numeric columns named e.g. "wellId" with values in the
 * 1000–9999 range stay as plain integers, not years.
 */
export function inferNumberFormat(values: number[], accessor?: string): NumberFormatHints {
  const nonZero = values.filter((v) => v !== 0);

  if (nonZero.length === 0) {
    return { scale: 0, useThousandSeparator: false };
  }

  const allInteger = values.every((v) => Number.isFinite(v) && Number.isInteger(v));
  const maxAbs = Math.max(...nonZero.map((v) => Math.abs(v)));

  // Year-like columns: 4-digit integers in plausible year range.
  // When an accessor is supplied we additionally require its name to mention
  // "year" — keeps unrelated 4-digit-id columns from being treated as years.
  const isYearLike =
    allInteger && nonZero.every((v) => v >= 1000 && v <= 9999) && (accessor === undefined || /year/i.test(accessor));

  if (isYearLike) {
    return { scale: 0, useThousandSeparator: false };
  }

  if (allInteger) {
    return { scale: 0, useThousandSeparator: maxAbs >= 1000 };
  }

  if (maxAbs >= 1000) {
    return { scale: 0, useThousandSeparator: true };
  }
  if (maxAbs >= 100) {
    return { scale: 1, useThousandSeparator: false };
  }
  if (maxAbs >= 1) {
    return { scale: 2, useThousandSeparator: false };
  }
  if (maxAbs >= 0.01) {
    return { scale: 3, useThousandSeparator: false };
  }
  return { scale: 4, useThousandSeparator: false };
}
/**
 * Formats a numeric value using {@link NumberFormatHints}.
 *
 * Canonical number→string formatter used by both table cells and chart
 * tooltips / axis ticks so display stays consistent across the dashboard.
 */
export function formatNumber(value: number, hints: NumberFormatHints): string {
  const options: Intl.NumberFormatOptions = { useGrouping: hints.useThousandSeparator };
  if (hints.scale !== undefined) {
    options.minimumFractionDigits = hints.scale;
    options.maximumFractionDigits = hints.scale;
  }
  return new Intl.NumberFormat('en-US', options).format(value);
}

/**
 * Infers format hints for a single field across a record array.
 *
 * Convenience wrapper over {@link inferNumberFormat} for the common chart-renderer
 * pattern: extract all numeric values from one column, then infer hints.
 */
export function inferFieldFormat(data: Record<string, unknown>[], accessor: string): NumberFormatHints {
  const values = data.map((row) => row[accessor]).filter((v): v is number => typeof v === 'number');
  return inferNumberFormat(values, accessor);
}
/**
 * Builds a string-returning formatter for a single numeric field.
 *
 * Convenience wrapper for chart renderers: infers format hints from the
 * column's values, returns a `(value: number) => string` suitable for
 * Mantine's `valueFormatter` prop or Recharts-style tick callbacks.
 */
export function makeFieldFormatter(data: Record<string, unknown>[], accessor: string): (value: number) => string {
  const hints = inferFieldFormat(data, accessor);
  return (value) => formatNumber(value, hints);
}
